import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Permissions, useScopeChecker } from 'src/rbac';
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Modal,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';

import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import SucessModal from 'src/components/sucess-modal';
import PencilIcon from 'src/icons/untitled-ui/duocolor/pencilIcon';
import {
  getMonthEndAlertCount,
  getOwnerAlertCount,
  resetCertifyAction,
} from 'src/redux/actions/monthEndAlertsAction';
import alertCicleIcon from '../../icons/untitled-ui/duocolor/alertCicleIcon.svg';
import alertDangerIcon from '../../icons/untitled-ui/duocolor/alertDangerIcon.svg';
import monthAlertIcon from '../../icons/untitled-ui/duocolor/monthAlertIcon.svg';
import monthEndAlertIcon from '../../icons/untitled-ui/duocolor/monthEndAlert.svg';
import { Chart } from './Chart';
import MonthAlertTableView from './MonthAlertTableView';
import ClosedPeriodActionBar from './ClosedPeriodActionBar';
import ClosedPeriodStatusPills from './ClosedPeriodStatusPills';
import { isHardClosedPeriod } from './closedPeriod';
import { useFinancialPeriodLock } from './useFinancialPeriodLock';
import CertifyModal from './certify-modal';

const MonthDayAlert = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const getMonthEndAlertData = useSelector(
    (state: any) => state?.getMonthEndAlertCountReducer?.response
  );
  const has = useScopeChecker();

  //  BOPS-170 — read once here rather than in each consumer, so the "Invoiced & locked" pill and
  //  the action bar below it can never disagree about whether finance has closed the books.
  const { isLocked: isFinancialPeriodLocked, isKnown: isFinancialPeriodLockKnown } =
    useFinancialPeriodLock(location?.state?.startDate, isHardClosedPeriod(location?.state));

  const checkLoading = useSelector((state: any) => state?.getMonthEndAlertCountReducer?.loading);
  const getOwnerAlertCountData = useSelector(
    (state: any) => state?.getOwnerAlertCountReducer?.response
  );

  const [activeButton] = useState('THIS_MONTH');
  //  BOPS-440 — "You've completed all your alerts!" next to a stopped download read as a
  //  contradiction (Kerry, 10/15), so the card is withheld while billing blockers remain.
  const [isExportBlocked, setIsExportBlocked] = useState(false);
  const getCertifyResponse = useSelector((state: any) => state.certifyMonthEndAlertReducer);
  const getCertificationStatusResponse = useSelector(
    (state: any) => state.getCertificationStatusReducer
  );

  useEffect(() => {
    dispatch(
      getMonthEndAlertCount({
        timePeriod: activeButton,
        param: {
          ...location?.state,
        },
      })
    );

    dispatch(
      getOwnerAlertCount({
        timePeriod: activeButton,
        param: {
          ...location?.state,
        },
      })
    );
  }, [activeButton]);

  const getColor = (index: number) => {
    const colors = ['#6941C6', '#A88BF8', '#EBDAFF', '#F4F0FA'];
    return colors[index % colors?.length];
  };

  const capitalizeFirstLetter = (str: any) => {
    return str?.toLowerCase().replace(/(?:^|\s)\S/g, (char: any) => char.toUpperCase());
  };

  const [openModal, setOpenModal] = useState(false);
  const closeCertifyModal = () => {
    setOpenModal(false);
  };

  const [openModalCertify, setOpenModalCertify] = useState({
    success: false,
    error: false,
  });

  const onCloseModal = () => {
    dispatch(resetCertifyAction());
    closeCertifyModal();
    setOpenModalCertify({
      success: false,
      error: false,
    });
  };

  useEffect(() => {
    if (!getCertifyResponse.isCertify && getCertifyResponse.response === null) return;

    if (getCertifyResponse.isCertify) {
      setOpenModalCertify({
        success: true,
        error: false,
      });
    } else {
      setOpenModalCertify({
        success: false,
        error: true,
      });
    }
  }, [getCertifyResponse]);

  const isLastDayOfMonth = (date: Date) => {
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getMonth() !== date.getMonth();
  };
  //  Hand the list back the tab and filters it was opened from. It keeps those in local state,
  //  so a bare navigate remounts it on Weekly however the user got here.
  const goBackToMonthlyCloseout = () => {
    navigate('/monthly-closeout-alerts', {
      state: { returnTo: (location?.state as any)?.returnTo },
    });
  };
  return (
    <Stack
      gap="2rem"
      sx={{ width: '100%', height: '100%', padding: '2rem 2rem 2rem 4rem' }}
    >
      <Grid xs={12}>
        <Stack gap="1.5rem">
          <Button
            sx={{
              justifyContent: 'flex-start',
              color: '#111927',
              width: 'max-content',
              padding: '0px',
            }}
            startIcon={
              <SvgIcon>
                <ArrowBackIcon sx={{ color: '#6C737F' }} />
              </SvgIcon>
            }
            onClick={goBackToMonthlyCloseout}
          >
            <Typography variant="subtitle1">Back to Closeout Alerts</Typography>
          </Button>
        </Stack>
      </Grid>
      <Stack gap="0.75rem">
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems={'center'}
        >
          <Typography variant="h4">
            {`${moment(location?.state?.startDate).format('MMM D, YYYY')} - ${moment(
              location?.state?.endDate
            ).format('MMM D, YYYY')}`}
          </Typography>
        </Stack>

        {/*  BOPS-170 — the pills sit under the heading, not beside it: "Invoiced & locked" only
             means anything read together with "Closed & signed", and a second pill on the heading
             row would be pushed to the far right by its space-between. */}
        <ClosedPeriodStatusPills
          dateRange={location?.state}
          isLocked={isFinancialPeriodLocked}
        />
      </Stack>

      <ClosedPeriodActionBar
        dateRange={location?.state}
        isLocked={isFinancialPeriodLocked}
        isLockKnown={isFinancialPeriodLockKnown}
        onExportBlockedChange={setIsExportBlocked}
      />

      {checkLoading || getCertificationStatusResponse.loading ? (
        <Stack flexDirection="column">
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </Stack>
      ) : getMonthEndAlertData?.totalAlertCount === 0 ? (
        isExportBlocked ? null : (
          <Grid container>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{
                margin: { xs: '2rem', sm: '4rem' },
                boxShadow:
                  '0px 5px 22px 0px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)',
                width: '100%',
                position: 'relative',
              }}
              spacing={{ xs: 2, sm: 4 }}
            >
              <img
                width="164px"
                height="150px"
                src={monthAlertIcon}
                alt="Alert Icon"
              />
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5">You&apos;ve completed all your alerts!</Typography>
              </Stack>
              {has(Permissions.CERTIFY_AND_SIGN_MONTH_END_ALERTS) &&
                isLastDayOfMonth(new Date()) &&
                !getCertificationStatusResponse.response?.data && (
                  <Stack
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'absolute',
                      right: 24,
                      top: '35%',
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        border: '1px solid rgba(56, 66, 80, 0.50)',
                        height: '42px',
                        borderRadius: '12px',
                        padding: '8px 22px 8px 22px',
                        cursor: 'pointer',
                        marginLeft: { xs: 0, sm: 'auto' },
                      }}
                      onClick={() => setOpenModal((prev) => !prev)}
                    >
                      <SvgIcon>
                        <PencilIcon />
                      </SvgIcon>
                      <Typography pl={1}>Sign a certification statement</Typography>
                    </Grid>
                  </Stack>
                )}
            </Stack>
          </Grid>
        )
      ) : (
        <Stack
          gap="3rem"
          flexDirection="row"
        >
          <Card
            sx={{
              padding: '24px',
              flex: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant="captionText"
                  fontWeight={600}
                >
                  THIS MONTH’S ALERTS
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Grid
                  container
                  display="flex"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Grid
                    item
                    xs={4}
                  >
                    <img
                      style={{ objectFit: 'cover' }}
                      width="102px"
                      height="121px"
                      src={monthEndAlertIcon}
                      alt=""
                    />
                  </Grid>
                  <Grid
                    item
                    lg={7}
                  >
                    <Grid
                      container
                      display="flex"
                      gap={1}
                    >
                      <Grid
                        item
                        xs={12}
                      >
                        <Typography variant="caption2Text">Total Alerts</Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                      >
                        <Typography
                          sx={{ lineHeight: '1' }}
                          variant="h2"
                        >
                          {getMonthEndAlertData?.totalAlertCount}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        gap={1}
                        paddingTop="12px"
                      >
                        <img
                          src={alertDangerIcon}
                          alt=""
                        />

                        <Typography
                          sx={{ color: '#384250' }}
                          variant="captionText"
                        >
                          {getMonthEndAlertData?.totalWarningCount} Warnings
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        gap={1}
                        paddingBottom="12px"
                      >
                        <img
                          src={alertCicleIcon}
                          alt=""
                        />

                        <Typography
                          sx={{ color: '#384250' }}
                          variant="captionText"
                        >
                          {getMonthEndAlertData?.totalErrorCount} Errors
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={5.5}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Typography variant="label">Required for closeout</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Stack sx={{ marginLeft: '.4rem' }}>
              <Stack
                flexDirection="row"
                gap=".4rem"
                alignItems="center"
              ></Stack>
              <Stack
                justifyContent="space-between"
                flexDirection="row"
              >
                <Stack
                  flexDirection="row"
                  gap=".4rem"
                  alignItems="center"
                ></Stack>
              </Stack>
            </Stack>
          </Card>
          <Card sx={{ flex: 7.5, display: 'flex', justifyContent: 'space-between' }}>
            <Stack sx={{ padding: '24px', width: '48%' }}>
              <Typography
                variant="captionText"
                fontWeight={600}
              >
                TOP-ALERT THEME
              </Typography>

              <Stack
                gap="1rem"
                alignItems="center"
                flexDirection="row"
              >
                <Chart
                  height={180}
                  width={180}
                  options={{
                    chart: {
                      background: 'transparent',
                    },
                    colors: ['#6941C6', '#A88BF8', '#EBDAFF', '#F4F0FA'],
                    dataLabels: {
                      enabled: false,
                    },
                    grid: {
                      padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      },
                    },
                    labels:
                      getMonthEndAlertData?.countByTheme?.map((item: any) => item?.name) || [],
                    legend: {
                      show: false,
                    },
                    plotOptions: {
                      pie: {
                        expandOnClick: false,
                        donut: {
                          size: '51%',
                        },
                      },
                      radialBar: {
                        dataLabels: {
                          show: false,
                        },
                        hollow: {
                          size: '100%',
                        },
                      },
                    },
                    states: {
                      active: {
                        filter: {
                          type: 'none',
                        },
                      },
                      hover: {
                        filter: {
                          type: 'none',
                        },
                      },
                    },
                    stroke: {
                      width: 0,
                    },
                    theme: {
                      // mode: theme.palette.mode,
                    },
                    tooltip: {
                      fillSeriesColor: false,
                    },
                  }}
                  series={getMonthEndAlertData?.countByTheme?.map((item: any) => item?.count) || []}
                  type="donut"
                />
                <Stack>
                  {getMonthEndAlertData?.countByTheme?.map((item: any, index: any) => (
                    <Stack
                      key={index}
                      gap=".5rem"
                      alignItems="center"
                      flexDirection="row"
                    >
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: getColor(index),
                        }}
                      />
                      <Typography
                        sx={{ color: '#667085' }}
                        variant="caption2Text"
                      >
                        {item?.count} {item?.name}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Divider sx={{ border: '1px solid #F2F4F7', height: '88%', margin: 'auto' }} />
            <Stack sx={{ width: '48%' }}>
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                sx={{ padding: '12px 24px' }}
                alignItems={'center'}
              >
                <Typography variant="captionText">TOP-ALERT OWNERS</Typography>
              </Stack>
              <Stack
                gap="1rem"
                sx={{ padding: '0 24px', overflowY: 'scroll', maxHeight: '200px', height: 'auto' }}
              >
                {getOwnerAlertCountData?.map((owner: any, index: any) => (
                  <Stack
                    key={index}
                    gap={'1rem'}
                    flexDirection="row"
                  >
                    <Avatar />
                    <Stack gap=".2rem">
                      <Typography variant="caption3Text">
                        {' '}
                        {capitalizeFirstLetter(owner?.name)}
                      </Typography>
                      <Typography variant="caption2Text">{`${owner?.count} alert${
                        owner.count !== 1 ? 's' : ''
                      }`}</Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Stack>
      )}

      <MonthAlertTableView
        dateRange={location?.state}
        filterOption={activeButton}
      />

      <Modal
        open={openModal}
        onClose={closeCertifyModal}
      >
        <CertifyModal
          close={closeCertifyModal}
          getMonthEndAlertData={getMonthEndAlertData}
        />
      </Modal>

      <Modal
        open={openModalCertify.success || openModalCertify.error}
        onClose={onCloseModal}
      >
        <SucessModal
          isSucess={openModalCertify.success ? true : false}
          title={openModalCertify.success ? 'Success' : 'Error'}
          message={
            openModalCertify.success
              ? 'Month end alert is certified successfully'
              : 'Month end alert could not be certified'
          }
          close={onCloseModal}
        />
      </Modal>
    </Stack>
  );
};

export default MonthDayAlert;
